import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ProjectItem } from '../../types/FJProject';

/** 首页「项目列表」子状态（与 landing 表单同域） */
export type ProjectsSubState = {
    items: ProjectItem[];
    /** 最近一次点击缩略图选中的项目 id */
    selectedProjectId: string | null;
    /** 下一次「加载更多」请求的 offset（与接口约定一致） */
    listNextOffset: number;
    /** 是否还有更多任务可拉取 */
    listHasMore: boolean;
    /** 正在加载下一页 */
    listLoadingMore: boolean;
    /** 任务列表接口返回的总条数（用于埋点等）；无 total 字段时为 null */
    listTotalCount: number | null;
    /** 最近文件多选：已勾选的项目 id */
    selectedProjectIds: string[];
};

export type StartLandingState = {
    /** 首页「粘贴链接」输入框内容，与输入受控绑定 */
    fileUrl: string;
    projects: ProjectsSubState;
    isVerifyingToGoogleDrive: boolean;
    /** 本地「浏览文件」解析视频元数据过程中为 true，用于上传按钮 loading 及禁用最近文件交互 */
    bFLoading: boolean;
};

/** 兼容旧命名 */
export type ProjectsState = ProjectsSubState;

const initialState: StartLandingState = {
    fileUrl: '',
    projects: {
        items: [],
        selectedProjectId: null,
        /** 下一页请求的 offset（接口为 1 起算；首屏拉取后由 reset 写入） */
        listNextOffset: 1,
        listHasMore: true,
        listLoadingMore: false,
        listTotalCount: null,
        selectedProjectIds: [],
    },
    isVerifyingToGoogleDrive: false,
    bFLoading: false,
};

const fjStartLandingSlice = createSlice({
    name: 'startLanding',
    initialState,
    reducers: {
        setFileUrl: (state, action: PayloadAction<string>) => {
            state.fileUrl = action.payload;
        },

        setProjects: (state, action: PayloadAction<ProjectItem[]>) => {
            state.projects.items = action.payload;
            const validIds = new Set(action.payload.map(i => String(i.id)));
            state.projects.selectedProjectIds = state.projects.selectedProjectIds
                .map(String)
                .filter(id => validIds.has(id));
        },
        /** 用接口整页结果替换列表并更新分页游标（首屏刷新、轮询、删除后同步等） */
        resetProjectsListPage: (
            state,
            action: PayloadAction<{
                items: ProjectItem[];
                nextOffset: number;
                hasMore: boolean;
                total: number | null;
            }>,
        ) => {
            state.projects.items = action.payload.items;
            state.projects.listNextOffset = action.payload.nextOffset;
            state.projects.listHasMore = action.payload.hasMore;
            state.projects.listTotalCount = action.payload.total;
            const validIds = new Set(action.payload.items.map(i => String(i.id)));
            state.projects.selectedProjectIds = state.projects.selectedProjectIds
                .map(String)
                .filter(id => validIds.has(id));
        },
        /** 追加一页并更新游标；按 id 去重，避免重叠区间重复 */
        appendProjectsListPage: (
            state,
            action: PayloadAction<{
                items: ProjectItem[];
                nextOffset: number;
                hasMore: boolean;
                total: number | null;
            }>,
        ) => {
            const seen = new Set(state.projects.items.map(i => i.id));
            for (const item of action.payload.items) {
                if (!seen.has(item.id)) {
                    state.projects.items.push(item);
                    seen.add(item.id);
                }
            }
            state.projects.listNextOffset = action.payload.nextOffset;
            state.projects.listHasMore = action.payload.hasMore;
            state.projects.listTotalCount = action.payload.total;
            const validIds = new Set(state.projects.items.map(i => String(i.id)));
            state.projects.selectedProjectIds = state.projects.selectedProjectIds
                .map(String)
                .filter(id => validIds.has(id));
        },
        setProjectListLoadingMore: (state, action: PayloadAction<boolean>) => {
            state.projects.listLoadingMore = action.payload;
        },
        removeProject: (state, action: PayloadAction<string>) => {
            state.projects.items = state.projects.items.filter(i => i.id !== action.payload);
            const rid = String(action.payload);
            state.projects.selectedProjectIds = state.projects.selectedProjectIds
                .map(String)
                .filter(id => id !== rid);
        },
        renameProject: (state, action: PayloadAction<{ id: string; title: string }>) => {
            const row = state.projects.items.find(i => i.id === action.payload.id);
            if (row) row.title = action.payload.title;
        },

        clickProjectThumb: (state, action: PayloadAction<string>) => {
            state.projects.selectedProjectId = action.payload;
        },
        setIsVerifyingToGoogleDrive: (state, action: PayloadAction<boolean>) => {
            state.isVerifyingToGoogleDrive = action.payload;
        },
        setBFLoading: (state, action: PayloadAction<boolean>) => {
            state.bFLoading = action.payload;
        },
        setSelectedProjectIds: (state, action: PayloadAction<string[]>) => {
            state.projects.selectedProjectIds = action.payload.map(String);
        },
        toggleProjectSelected: (state, action: PayloadAction<string>) => {
            const id = String(action.payload);
            const list = state.projects.selectedProjectIds.map(String);
            const index = list.indexOf(id);
            if (index >= 0) {
                list.splice(index, 1);
            } else {
                list.push(id);
            }
            state.projects.selectedProjectIds = list;
        },
        clearProjectSelection: state => {
            state.projects.selectedProjectIds = [];
        },
    },
});

export const {
    setFileUrl,
    setProjects,
    resetProjectsListPage,
    appendProjectsListPage,
    setProjectListLoadingMore,
    removeProject,
    renameProject,
    clickProjectThumb,
    setIsVerifyingToGoogleDrive,
    setBFLoading,
    setSelectedProjectIds,
    toggleProjectSelected,
    clearProjectSelection,
} = fjStartLandingSlice.actions;

export default fjStartLandingSlice.reducer;
